/**
 * BrandSimulator
 *
 * Sandbox interactivo donde el usuario ve cómo SUS reglas afectan el
 * mensaje que generaría la IA en distintos escenarios y canales.
 *
 * - 7 escenarios alineados con los presets de Objetivos del producto +
 *   contextos típicos de un restaurante:
 *     1. Carrito abandonado
 *     2. Reactivación cliente VIP
 *     3. Aumentar frecuencia
 *     4. Subir ticket promedio
 *     5. Onboarding primera compra
 *     6. Queja por demora
 *     7. Cumpleaños
 *
 * - Tabs de canal: WhatsApp (burbuja verde) / Push (banner móvil chico).
 *   El estilo del mensaje cambia según canal: WhatsApp más largo, Push corto.
 *
 * - Aplica las reglas: tono → fraseo / agresividad → urgencia + descuento /
 *   forbiddenWords → bloquea con badge si aparecen.
 *
 * Mock por ahora — al conectar el backend se reemplaza por una llamada al
 * mismo prompt builder que usan los Objetivos reales.
 */

import { forwardRef, useEffect, useState } from "react";
import {
    MessageSquare,
    Activity,
    Scale,
    Sliders,
    ShieldCheck,
    Eye,
    Loader2,
    PlayCircle,
    Bell,
    MessageCircle,
} from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";
import { Button } from "../../../shared/components/ui/Button";

interface BrandSimulatorProps {
    tone: number;
    aggressiveness: number;
    forbiddenWords: string[];
    brandName?: string;
}

type Channel = "whatsapp" | "push";

interface Scenario {
    id: string;
    label: string;
    presetKey: string;
    context: string;
    group: "efemerides" | "behavioral";
}

const SCENARIOS: Scenario[] = [
    // ─── Efemérides ───
    {
        id: "valentines",
        label: "💝 Día de los Enamorados",
        presetKey: "valentines",
        context: "Pareja que pidió delivery hace 2 semanas. 14/2 en 4 días.",
        group: "efemerides",
    },
    {
        id: "mothers_day",
        label: "👩 Día de la Madre",
        presetKey: "mothers_day",
        context: "Cliente recurrente. 3er dom de octubre, faltan 12 días.",
        group: "efemerides",
    },
    {
        id: "fathers_day",
        label: "👨 Día del Padre",
        presetKey: "fathers_day",
        context: "Familia que reservó el año pasado. 3er dom de junio.",
        group: "efemerides",
    },
    {
        id: "friends_day",
        label: "🍻 Día del Amigo",
        presetKey: "friends_day",
        context: "Grupo que reservó mesa de 8 el 20/7 del año pasado.",
        group: "efemerides",
    },
    {
        id: "kids_day",
        label: "🧒 Día del Niño",
        presetKey: "kids_day",
        context: "Familia con hijos chicos. Cliente recurrente.",
        group: "efemerides",
    },
    {
        id: "workers_day",
        label: "🛠️ Día del Trabajador",
        presetKey: "workers_day",
        context: "1 de mayo. Cliente pidió 3 veces los últimos 30 días.",
        group: "efemerides",
    },
    {
        id: "christmas_newyear",
        label: "🎄 Navidad y Año Nuevo",
        presetKey: "christmas_newyear",
        context: "Cliente VIP LTV $80k. 22 de diciembre.",
        group: "efemerides",
    },
    {
        id: "birthday",
        label: "🎂 Cumpleaños del cliente",
        presetKey: "birthday",
        context: "Sofía cumple 30 hoy. Cliente con LTV alto.",
        group: "efemerides",
    },
    {
        id: "customer_anniversary",
        label: "🎉 Aniversario como cliente",
        presetKey: "customer_anniversary",
        context: "Matías cumple 1 año como cliente. 18 pedidos.",
        group: "efemerides",
    },
    // ─── Comportamentales ───
    {
        id: "cart_abandon",
        label: "🛒 Carrito abandonado",
        presetKey: "cart_abandon",
        context: "Lucía dejó orden de $4.500 (milanesas + papas) hace 2h.",
        group: "behavioral",
    },
    {
        id: "winback_vip",
        label: "🔁 Reactivar VIP",
        presetKey: "winback_vip",
        context: "Matías VIP (LTV $48k) sin pedir hace 35 días.",
        group: "behavioral",
    },
    {
        id: "purchase_frequency",
        label: "📈 Subir frecuencia",
        presetKey: "purchase_frequency",
        context: "Juana pidió 2 veces este mes. Target: 4 al mes.",
        group: "behavioral",
    },
    {
        id: "aov_increase",
        label: "💸 Subir ticket promedio",
        presetKey: "aov_increase",
        context: "Cliente con ticket promedio $2.800 vs $4.200 de la BD.",
        group: "behavioral",
    },
    {
        id: "welcome_first",
        label: "👋 Bienvenida primera compra",
        presetKey: "welcome_first",
        context: "Sofía hizo su primer pedido ayer. Llegó hace 1h.",
        group: "behavioral",
    },
    {
        id: "winback_general",
        label: "😴 Despertar inactivo",
        presetKey: "winback_general",
        context: "Diego sin pedir hace 78 días. Antes pedía 1x semana.",
        group: "behavioral",
    },
    {
        id: "cross_sell",
        label: "🛍️ Cross-sell post-pedido",
        presetKey: "cross_sell",
        context: "Pidió milanesas + papas hace 3 días. No probó postres.",
        group: "behavioral",
    },
    {
        id: "reorder_reminder",
        label: "⏰ Próximo pedido estimado",
        presetKey: "reorder_reminder",
        context: "Ana pide en promedio cada 9 días. Mañana es día 9.",
        group: "behavioral",
    },
    {
        id: "review_request",
        label: "⭐ Pedir review",
        presetKey: "review_request",
        context: "Bruno recibió su pedido hace 2 días. Sin queja registrada.",
        group: "behavioral",
    },
];

function buildMessage(
    scenarioId: string,
    tone: number,
    aggressiveness: number,
    channel: Channel,
): { title?: string; body: string } {
    const isFormal = tone < 30;
    const isFriendly = tone > 70;
    const isAggro = aggressiveness > 70;
    const isConservative = aggressiveness < 40;

    // ── WhatsApp (long form) ──
    if (channel === "whatsapp") {
        switch (scenarioId) {
            case "valentines":
                if (isAggro)
                    return {
                        body: "💝 ¡¡Mesa para 2 el 14 de febrero!! 30% OFF en menú degustación + copa de champagne GRATIS. Reservá YA, quedan pocas mesas 🔥",
                    };
                if (isFormal)
                    return {
                        body: "Estimado, le informamos que para el Día de los Enamorados ofrecemos un menú especial de dos pasos con maridaje. Reservas al 0810-LABIS.",
                    };
                return {
                    body: "💝 Para San Valentín preparamos un menú especial para dos. Reservá tu mesa antes que se llene, queda una semana 🍝",
                };
            case "mothers_day":
                if (isAggro)
                    return {
                        body: "👩 ¡EL DÍA DE LA MADRE SE ACABA! Mesa familiar a $35.000 (8 personas) + postre gratis. Últimos cupos disponibles ⏰",
                    };
                return {
                    body: "👩 Llegó octubre y se viene el Día de la Madre. Reservá la mesa familiar antes que se complete — el año pasado tuvimos lista de espera 💜",
                };
            case "fathers_day":
                if (isAggro)
                    return {
                        body: "👨 ¡El asado del Día del Padre te espera! Bife de chorizo + costillar + 4 guarniciones a $28.000 para 4 personas. Reservas abiertas 🔥",
                    };
                return {
                    body: "👨 Domingo del Padre. Preparamos parrilla completa para juntar a la familia. Reservá tu mesa, tenemos cortes premium esa fecha 🥩",
                };
            case "friends_day":
                if (isAggro)
                    return {
                        body: "🍻 ¡20 DE JULIO! Tabla compartida + cerveza ilimitada x 2hs por $9.500 cada uno. Mínimo 4 personas. Reservá YA — el año pasado se llenó 🔥",
                    };
                return {
                    body: "🍻 Para el Día del Amigo armamos tabla compartida + cerveza tirada. Ideal para grupos de 6+ personas. Reservá la mesa para la noche del 20 🍴",
                };
            case "kids_day":
                if (isFriendly)
                    return {
                        body: "🧒 ¡Llega el Día del Niño! Menú infantil con regalito sorpresa + animación para los chicos este domingo. Familias bienvenidas 🎈",
                    };
                return {
                    body: "🧒 Este domingo es el Día del Niño. Preparamos menú infantil + sorpresita. Esperamos a tu familia 💜",
                };
            case "workers_day":
                return isAggro
                    ? {
                          body: "🛠️ ¡1 DE MAYO! Menú trabajador a $4.500 con bebida incluida. Abierto en feriado todo el día. Reservá 🔥",
                      }
                    : {
                          body: "🛠️ El 1 de mayo abrimos con menú especial trabajador. Ideal para almorzar en familia el feriado 🍝",
                      };
            case "christmas_newyear":
                if (isAggro)
                    return {
                        body: "🎄 ¡CENA DE NAVIDAD! Vitel toné + lechón + pavita rellena + postre. Para 6 personas $42.000. Pedidos hasta el 22/12 — quedan pocos cupos 🔥",
                    };
                return {
                    body: "🎄 Para las fiestas armamos pedidos pre-encargados para llevar. Vitel toné, lechón, postres caseros. Reservá hasta el 22/12 ✨",
                };
            case "birthday":
                if (isAggro)
                    return {
                        body: "🎂 ¡¡FELIZ CUMPLE SOFÍA!! Ordená HOY y reclamá postre GRATIS + 20% OFF en TODO. Solo válido hoy. ¡No te lo pierdas!",
                    };
                return {
                    body: "🎂 ¡Feliz cumple Sofía! Hoy un postre va de regalo si pedís cualquier menú principal. Que tengas un día hermoso 💜",
                };
            case "customer_anniversary":
                return {
                    body: "🎉 Matías, hace 1 año que sos cliente de La Bistecca y queríamos agradecerte. 18 pedidos después… te dejamos 15% OFF en tu próximo pedido como gracias 💜",
                };
            // ─── Behavioral ───
            case "cart_abandon":
                if (isAggro)
                    return {
                        body: "🛒 ¡Lucía, última oportunidad! Tus milanesas + papas te esperan. Completá AHORA y llevate 10% OFF + envío gratis 🔥",
                    };
                if (isFormal)
                    return {
                        body: "Hola, notamos que dejó una orden pendiente en su carrito. ¿Necesita asistencia para completarla?",
                    };
                return {
                    body: "🛒 Lucía, te quedaron unas milanesas olvidadas. Las guardamos en tu carrito por si todavía tenés hambre 🍽️",
                };
            case "winback_vip":
                if (isAggro)
                    return {
                        body: "🔁 ¡Te extrañamos Matías! 30% OFF + envío gratis en tu próximo pedido. Solo por 48h ⏰",
                    };
                if (isConservative)
                    return {
                        body: "Matías, hace un tiempo que no nos vemos. Si querés volver a pedir, tu envío va por nuestra cuenta 💜",
                    };
                return {
                    body: "Matías, hace 5 semanas que no pasás. Te dejamos 20% OFF para que vuelvas cuando quieras 🍝",
                };
            case "purchase_frequency":
                if (isAggro)
                    return {
                        body: "📈 Pedís 2 veces al mes — ¿por qué no 4? Esta semana: 15% OFF si volvés a pedir antes del viernes",
                    };
                return {
                    body: "📈 Juana, vimos que disfrutás La Bistecca un par de veces al mes 💜 Probaste nuestro nuevo menú del día?",
                };
            case "aov_increase":
                if (isAggro)
                    return {
                        body: "💸 Combo familia: 4 platos + 2 postres + bebida a $6.900. Te ahorrás $1.800 vs comprar suelto 🍕",
                    };
                return {
                    body: "💸 Si sumás un postre casero a tu próximo pedido lleva solo $900 más. Hecho en el día 🍮",
                };
            case "welcome_first":
                if (isFriendly)
                    return {
                        body: "👋 ¡Bienvenida Sofía a La Bistecca! Esperamos que disfrutes tu primer pedido 💜 Para el próximo: 15% OFF + envío gratis usando WELCOME15",
                    };
                if (isFormal)
                    return {
                        body: "Estimada Sofía, le agradecemos su primer pedido en La Bistecca. Para su próxima compra dispone de 15% de descuento.",
                    };
                return {
                    body: "👋 Sofía, gracias por tu primer pedido. Si querés probar otra cosa, la próxima va con 15% OFF + envío gratis 🇮🇹",
                };
            case "winback_general":
                if (isAggro)
                    return {
                        body: "😴 Diego, hace 78 días que no pedís. Te tiramos 25% OFF + envío gratis para que vuelvas. Última chance antes de que te archivemos 😉",
                    };
                return {
                    body: "😴 Diego, hace tiempo que no nos vemos. Si querés volver a pedir, tenés 20% OFF y envío sin cargo 🍝",
                };
            case "cross_sell":
                if (isAggro)
                    return {
                        body: "🛍️ Si te gustaron las milanesas con papas, ¡no podés perderte nuestro flan casero! 30% OFF en tu próximo pedido sumando postre 🍮",
                    };
                return {
                    body: "🛍️ Vimos que probaste las milanesas. ¿Te animás a probar el flan casero? Va de regalo en tu próximo pedido 💜",
                };
            case "reorder_reminder":
                if (isAggro)
                    return {
                        body: "⏰ Ana, sabemos que ya casi es tu día de pedido. Tu plato favorito te espera — pedí ahora con 10% OFF antes de que se acabe el día 🍝",
                    };
                return {
                    body: "⏰ Ana, hace 9 días que pediste y sabemos que ese es tu intervalo habitual. Si tenés hambre, estamos listos 🍝",
                };
            case "review_request":
                return isAggro
                    ? {
                          body: "⭐ Bruno, ¿qué tal estuvo tu pedido del miércoles? Si te gustó, dejanos una review en Google. Te damos 10% OFF en el próximo pedido como gracias 🙏",
                      }
                    : {
                          body: "⭐ Bruno, esperamos que hayas disfrutado tu pedido. Si tenés un minuto, dejanos una review en Google Maps. ¡Nos ayuda muchísimo! 🙏",
                      };
        }
    }

    // ── Push (short form) ──
    switch (scenarioId) {
        case "valentines":
            return isAggro
                ? { title: "💝 Mesa para 2 el 14/2", body: "30% OFF + champagne. Últimos cupos 🔥" }
                : { title: "💝 Día de los Enamorados", body: "Reservá tu mesa para dos antes que se llene" };
        case "mothers_day":
            return { title: "👩 Día de la Madre", body: "Reservá la mesa familiar — el año pasado tuvimos lista de espera" };
        case "fathers_day":
            return { title: "👨 Asado del Padre", body: "Parrilla completa para la familia. Domingo." };
        case "friends_day":
            return isAggro
                ? { title: "🍻 ¡20 de julio!", body: "Tabla + cerveza 2h a $9.500. Reservá YA" }
                : { title: "🍻 Día del Amigo", body: "Mesas grandes con tabla compartida. Reservá grupo." };
        case "kids_day":
            return { title: "🧒 Día del Niño", body: "Menú infantil + sorpresita este domingo 🎈" };
        case "workers_day":
            return { title: "🛠️ 1 de mayo", body: "Abierto en feriado con menú trabajador" };
        case "christmas_newyear":
            return isAggro
                ? { title: "🎄 Cena de Navidad", body: "Pedido para 6: vitel toné + lechón + postre $42.000" }
                : { title: "🎄 Pedidos para las fiestas", body: "Vitel toné, lechón, postres caseros. Reservá hasta 22/12" };
        case "birthday":
            return isAggro
                ? { title: "🎂 ¡Feliz cumple Sofía!", body: "Postre GRATIS + 20% OFF en TODO hoy" }
                : { title: "🎂 ¡Feliz cumple Sofía!", body: "Postre de regalo si pedís hoy" };
        case "customer_anniversary":
            return { title: "🎉 1 año como cliente", body: "15% OFF en tu próximo pedido como gracias 💜" };
        // ─── Behavioral (push form) ───
        case "cart_abandon":
            return isAggro
                ? { title: "🛒 ¡Última oportunidad!", body: "Tus milanesas te esperan. 10% OFF si terminás ya 🔥" }
                : { title: "🛒 Tu pedido te espera", body: "Quedaron unas milanesas en tu carrito" };
        case "winback_vip":
            return isAggro
                ? { title: "🔁 Te extrañamos 💜", body: "30% OFF + envío gratis solo por 48h" }
                : { title: "🔁 ¿Volvemos a vernos?", body: "Tu envío va por nuestra cuenta cuando quieras pedir" };
        case "purchase_frequency":
            return { title: "📈 Nuevo menú del día", body: "Probá la pasta especial del chef esta semana 🍝" };
        case "aov_increase":
            return isAggro
                ? { title: "💸 Combo familia $6.900", body: "Te ahorrás $1.800 vs comprar suelto" }
                : { title: "💸 ¿Sumás postre?", body: "Postre casero del día por solo $900 🍮" };
        case "welcome_first":
            return { title: "👋 ¡Gracias Sofía!", body: "15% OFF + envío gratis en tu próximo pedido 🎉" };
        case "winback_general":
            return isAggro
                ? { title: "😴 Última chance", body: "25% OFF + envío gratis. Volvé hoy 🍝" }
                : { title: "😴 Hace tiempo que no venís", body: "20% OFF si te animás a pedir" };
        case "cross_sell":
            return { title: "🛍️ Probá nuestro flan", body: "Va de regalo si pedís hoy 🍮" };
        case "reorder_reminder":
            return { title: "⏰ Hoy es tu día", body: "Pediste hace 9 días — listos cuando quieras 🍝" };
        case "review_request":
            return isAggro
                ? { title: "⭐ Tu review nos ayuda", body: "Dejá review en Google y 10% OFF próximo pedido 🙏" }
                : { title: "⭐ ¿Qué tal el pedido?", body: "Dejanos una review en Google Maps 🙏" };
    }
    return { body: "Mensaje generado por la IA" };
}

const BrandSimulator = forwardRef<HTMLDivElement, BrandSimulatorProps>(
    ({ tone, aggressiveness, forbiddenWords, brandName }, ref) => {
        // Audit B-10: iniciales reales de la marca para el preview de push.
        const brandInitials =
            (brandName || "")
                .trim()
                .split(/\s+/)
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase() || "LB";
        const [scenarioId, setScenarioId] = useState<string>("valentines");
        const [channel, setChannel] = useState<Channel>("whatsapp");
        const [isSimulating, setIsSimulating] = useState(false);
        const [output, setOutput] = useState<{
            title?: string;
            body: string;
            blocked?: string;
        } | null>(null);

        // Audit B-01/B-07: preview "En vivo" de verdad — se regenera solo al
        // cambiar escenario, canal o las reglas (tono / estrategia / palabras).
        useEffect(() => {
            const msg = buildMessage(scenarioId, tone, aggressiveness, channel);
            const fullText = `${msg.title || ""} ${msg.body}`.toLowerCase();
            const blocked = forbiddenWords.find((w) =>
                fullText.includes(w.toLowerCase()),
            );
            setOutput({ ...msg, blocked: blocked || undefined });
        }, [scenarioId, channel, tone, aggressiveness, forbiddenWords]);

        const handleSimulate = () => {
            setIsSimulating(true);
            setTimeout(() => {
                const msg = buildMessage(
                    scenarioId,
                    tone,
                    aggressiveness,
                    channel,
                );
                const fullText = `${msg.title || ""} ${msg.body}`.toLowerCase();
                const blocked = forbiddenWords.find((w) =>
                    fullText.includes(w.toLowerCase()),
                );
                setOutput({
                    ...msg,
                    blocked: blocked || undefined,
                });
                setIsSimulating(false);
            }, 1200);
        };

        return (
            <Card
                className="p-5 sticky top-4 border-violet-100/50 shadow-[0_0_40px_rgba(139,92,246,0.1)] flex flex-col gap-5"
            >
                <div ref={ref} />

                {/* Header */}
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-violet-400 to-fuchsia-400 rounded-lg text-white shadow-md">
                        <MessageSquare size={18} />
                    </div>
                    <div>
                        <h3 className="text-slate-900 font-bold text-lg">
                            Simulador
                        </h3>
                        <p className="text-xs text-slate-500">
                            Probá tu IA en vivo con escenarios reales
                        </p>
                    </div>
                </div>

                {/* Active config summary */}
                <div className="bg-slate-50 p-3 rounded-xl border border-slate-200/60 space-y-2">
                    <h4 className="text-[10px] text-slate-500 uppercase font-bold tracking-wider flex items-center gap-1.5 mb-1.5 border-b border-slate-200 pb-1.5">
                        <Activity size={11} className="text-violet-500" />
                        Configuración activa
                    </h4>
                    <div className="flex justify-between text-xs">
                        <span className="text-slate-600 flex items-center gap-1.5">
                            <Sliders size={11} /> Tono
                        </span>
                        <span className="text-slate-900 font-mono font-bold">
                            {tone}%
                        </span>
                    </div>
                    <div className="flex justify-between text-xs">
                        <span className="text-slate-600 flex items-center gap-1.5">
                            <Scale size={11} /> Estrategia
                        </span>
                        <span
                            className={`font-mono font-bold ${
                                aggressiveness > 70
                                    ? "text-red-500"
                                    : aggressiveness < 40
                                      ? "text-emerald-600"
                                      : "text-slate-900"
                            }`}
                        >
                            {aggressiveness > 70
                                ? "Agresiva"
                                : aggressiveness < 40
                                  ? "Conservadora"
                                  : "Balanceada"}
                        </span>
                    </div>
                    <div>
                        <div className="text-[10px] text-slate-500 mb-1 flex items-center gap-1.5 font-medium">
                            <ShieldCheck size={11} /> Palabras filtradas
                        </div>
                        <div className="flex flex-wrap gap-1">
                            {forbiddenWords.length > 0 ? (
                                forbiddenWords.slice(0, 4).map((w) => (
                                    <span
                                        key={w}
                                        className="px-1.5 py-0.5 rounded bg-red-50 text-red-400 text-[9px] border border-red-100 line-through"
                                    >
                                        {w}
                                    </span>
                                ))
                            ) : (
                                <span className="text-[9px] text-slate-400 italic">
                                    Ninguna
                                </span>
                            )}
                            {forbiddenWords.length > 4 && (
                                <span className="text-[9px] text-slate-400">
                                    +{forbiddenWords.length - 4}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Channel tabs */}
                <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
                    <button
                        onClick={() => setChannel("whatsapp")}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                            channel === "whatsapp"
                                ? "bg-white text-green-700 shadow-sm"
                                : "text-slate-600 hover:text-slate-900"
                        }`}
                    >
                        <MessageCircle size={12} />
                        WhatsApp
                    </button>
                    <button
                        onClick={() => setChannel("push")}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                            channel === "push"
                                ? "bg-white text-blue-700 shadow-sm"
                                : "text-slate-600 hover:text-slate-900"
                        }`}
                    >
                        <Bell size={12} />
                        Push
                    </button>
                </div>

                {/* Scenario picker — agrupado en Efemérides / Comportamentales */}
                <div className="space-y-2">
                    <label className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                        Escenario
                    </label>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                        {(["efemerides", "behavioral"] as const).map((group) => {
                            const items = SCENARIOS.filter(
                                (s) => s.group === group,
                            );
                            return (
                                <div key={group} className="space-y-1">
                                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 px-1">
                                        {group === "efemerides"
                                            ? "📅 Por fecha"
                                            : "🔁 Comportamentales"}
                                    </div>
                                    {items.map((s) => (
                                        <button
                                            key={s.id}
                                            onClick={() => setScenarioId(s.id)}
                                            className={`w-full text-left p-2 rounded-lg border text-xs transition-all duration-200 ${
                                                scenarioId === s.id
                                                    ? "bg-violet-600 border-violet-600 text-white shadow-sm"
                                                    : "bg-white border-slate-100 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
                                            }`}
                                        >
                                            <div className="font-semibold">
                                                {s.label}
                                            </div>
                                            <div
                                                className={`text-[10px] mt-0.5 line-clamp-1 ${
                                                    scenarioId === s.id
                                                        ? "text-violet-100"
                                                        : "text-slate-500"
                                                }`}
                                            >
                                                {s.context}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Preview */}
                <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200/60 p-3 flex flex-col min-h-[140px]">
                    <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider mb-2 flex justify-between">
                        <span>Vista previa</span>
                        <span className="text-emerald-600 flex items-center gap-1">
                            <Eye size={10} /> En vivo
                        </span>
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                        {isSimulating ? (
                            <div className="flex gap-1 py-3 items-center">
                                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce" />
                                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce delay-75" />
                                <span className="w-1.5 h-1.5 bg-violet-500 rounded-full animate-bounce delay-150" />
                            </div>
                        ) : output ? (
                            output.blocked ? (
                                <BlockedPreview word={output.blocked} />
                            ) : channel === "whatsapp" ? (
                                <WhatsAppBubble body={output.body} />
                            ) : (
                                <PushBanner
                                    title={output.title || "Notificación"}
                                    body={output.body}
                                    initials={brandInitials}
                                />
                            )
                        ) : (
                            <p className="text-xs text-slate-400 italic text-center px-4">
                                Elegí un escenario y dale a "Generar"
                            </p>
                        )}
                    </div>
                </div>

                <Button
                    onClick={handleSimulate}
                    className="w-full"
                    disabled={isSimulating}
                >
                    {isSimulating ? (
                        <Loader2 className="animate-spin" size={16} />
                    ) : (
                        <PlayCircle size={16} />
                    )}
                    Generar vista previa
                </Button>
            </Card>
        );
    },
);

BrandSimulator.displayName = "BrandSimulator";
export default BrandSimulator;

/* ─── Sub-components: previews por canal ─── */
function WhatsAppBubble({ body }: { body: string }) {
    return (
        <div className="bg-[#e1fbc4] p-3 rounded-lg rounded-tl-none w-full shadow-sm text-slate-900 text-sm leading-relaxed border-l-4 border-green-500 font-sans">
            {body}
        </div>
    );
}

function PushBanner({ title, body, initials }: { title: string; body: string; initials: string }) {
    return (
        <div className="w-full bg-white rounded-xl shadow-md border border-slate-200 p-3 flex items-start gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {initials}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-0.5">
                    <p className="text-xs font-bold text-slate-900 truncate">
                        {title}
                    </p>
                    <span className="text-[9px] text-slate-400 flex-shrink-0">
                        ahora
                    </span>
                </div>
                <p className="text-[11px] text-slate-600 line-clamp-2">{body}</p>
            </div>
        </div>
    );
}

function BlockedPreview({ word }: { word: string }) {
    return (
        <div className="bg-red-50 border-2 border-red-200 border-dashed rounded-lg p-3 w-full text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-red-500 mb-1">
                Bloqueado por regla
            </p>
            <p className="text-xs text-red-700">
                El mensaje contenía la palabra prohibida{" "}
                <span className="font-bold">"{word}"</span>
            </p>
            <p className="text-[10px] text-red-600/70 mt-1">
                La IA reintentará con una versión alternativa.
            </p>
        </div>
    );
}
